/**
 * Project Name: Brainsie eCommerce v4
 * Component: Default Paragraph
 * Description: Paragraph text component for infographics
 * Author: Brainsie Team
 * Date Created: October 29, 2025
 */

import React from "react";
import { Typography, Box } from "@mui/material";

export default function DefaultParagraph({
  component,
  section,
  darkMode,
  theme,
  settings = {},
  renderListContent,
  renderCTAButtons,
}) {
  // Get component data using correct field mapping
  const headingText = component.Primary_Text || "";
  const secondaryText = component.Secondary_Text || "";
  const paragraphText = component.Descriptive_Text || "";
  const listContent = component.List_Content || "";
  const primaryCTA = component.Primary_CTA || "";
  const secondaryCTA = component.Secondary_CTA || "";

  // Enhanced component settings with comprehensive defaults
  const componentSettings = {
    size: "medium",
    variant: "contained",
    color: "primary",
    fullWidth: false,
    disabled: false,
    fontSize: "",
    fontWeight: "",
    textAlign: "left",
    backgroundColor: "",
    textColor: "",
    borderRadius: "",
    padding: "",
    margin: "",
    elevation: 0,
    customStyles: "",
    ...settings,
    ...(component.Component_Settings
      ? typeof component.Component_Settings === "string"
        ? JSON.parse(component.Component_Settings)
        : component.Component_Settings
      : {}),
  };

  // Comprehensive styling functions
  const getTextColor = () =>
    componentSettings.textColor ||
    (darkMode ? theme.palette.common.white : theme.palette.text.primary);

  const getHeadingSize = () => {
    if (componentSettings.fontSize) return componentSettings.fontSize;
    return componentSettings.size === "large"
      ? "1.5rem"
      : componentSettings.size === "small"
      ? "1rem"
      : "1.25rem";
  };

  const getBodySize = () => {
    if (componentSettings.fontSize) return componentSettings.fontSize;
    return componentSettings.size === "large"
      ? "1.1rem"
      : componentSettings.size === "small"
      ? "0.875rem"
      : "1rem";
  };

  const getComponentStyles = () => ({
    mb: componentSettings.margin ? 0 : 2,
    padding: componentSettings.padding || 0,
    margin: componentSettings.margin || "0 0 16px 0",
    backgroundColor: componentSettings.backgroundColor || "transparent",
    borderRadius: componentSettings.borderRadius || "0px",
    textAlign: componentSettings.textAlign || "left",
    width: componentSettings.fullWidth ? "100%" : "auto",
    opacity: componentSettings.disabled ? 0.6 : 1,
    boxShadow:
      componentSettings.elevation > 0
        ? `0px ${componentSettings.elevation}px ${
            componentSettings.elevation * 2
          }px rgba(0,0,0,0.12)`
        : "none",
    ...(componentSettings.customStyles
      ? (() => {
          try {
            return typeof componentSettings.customStyles === "string"
              ? JSON.parse(componentSettings.customStyles)
              : componentSettings.customStyles;
          } catch {
            return {};
          }
        })()
      : {}),
  });

  // Don't render if no meaningful content
  if (
    (!headingText || headingText === "NA") &&
    !paragraphText &&
    !listContent &&
    !primaryCTA &&
    !secondaryCTA
  ) {
    return null;
  }

  return (
    <Box sx={getComponentStyles()}>
      {/* Primary Text (Main Heading) */}
      {headingText && headingText !== "NA" && (
        <Typography
          variant="h5"
          component="h3"
          sx={{
            mb: 1.5,
            color: getTextColor(),
            fontWeight: componentSettings.fontWeight || 600,
            fontSize: getHeadingSize(),
            textAlign: componentSettings.textAlign || "left",
          }}
        >
          {headingText}
        </Typography>
      )}

      {/* Secondary Text (Sub Heading) */}
      {secondaryText && secondaryText !== "NA" && (
        <Typography
          variant="h6"
          component="h4"
          sx={{
            mb: 1,
            color: getTextColor(),
            fontWeight: componentSettings.fontWeight || 500,
            fontSize:
              componentSettings.size === "large"
                ? "1.2rem"
                : componentSettings.size === "small"
                ? "0.9rem"
                : "1rem",
            textAlign: componentSettings.textAlign || "left",
            opacity: 0.9,
          }}
        >
          {secondaryText}
        </Typography>
      )}

      {/* Descriptive Text (Main Content) */}
      {paragraphText && (
        <Typography
          variant="body1"
          component="p"
          sx={{
            mb: listContent || primaryCTA || secondaryCTA ? 2 : 0,
            color: getTextColor(),
            lineHeight: 1.6,
            fontSize: getBodySize(),
            fontWeight: componentSettings.fontWeight || "normal",
            textAlign: componentSettings.textAlign || "left",
          }}
        >
          {paragraphText}
        </Typography>
      )}

      {/* List Content (JSON Array) */}
      {listContent && renderListContent && renderListContent(listContent)}

      {/* CTA Buttons */}
      {(primaryCTA || secondaryCTA) &&
        renderCTAButtons &&
        renderCTAButtons(primaryCTA, secondaryCTA)}
    </Box>
  );
}
